/**
 * MappingSetNotice — the line an import or export shows before it runs: which
 * Field Mappings set applies, what it maps, and where to change it. Warns when
 * a rule targets an attribute this workspace does not have.
 *
 * Pass `active` when the caller already resolved the set; otherwise pass
 * `workspaceId` and the notice loads it (fieldMappingApply.resolveActiveMappingSet).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { loadFieldMappingConfig } from '../../../lib/fieldMappingRegistry';
import {
  attributeKeyOfTarget,
  describeMappingSetRules,
  resolveActiveMappingSet,
  ruleRunsOn,
  type ActiveMappingSet,
  type MappingFlow,
} from '../../../lib/fieldMappingApply';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';

const FIELD_MAPPINGS_PATH = '/prioritization/settings/field-mappings';
const ATTRIBUTES_PATH = '/prioritization/settings/attributes';

export interface MappingSetNoticeProps {
  connectorType: string;
  workspaceId?: string | null;
  /** The resolved set, when the caller has it. `undefined` = load it here. */
  active?: ActiveMappingSet | null;
  flow?: MappingFlow;
  /** Shown when no set applies. Nothing is shown when omitted. */
  emptyText?: ReactNode;
  /** Warn about rules whose attribute this workspace lacks (rules that write accepted values). */
  checkAttributes?: boolean;
  className?: string;
}

export function MappingSetNotice({
  connectorType,
  workspaceId,
  active: given,
  flow = 'import',
  emptyText,
  checkAttributes = true,
  className = '',
}: MappingSetNoticeProps) {
  const [loaded, setLoaded] = useState<ActiveMappingSet | null>(null);
  const attributes = useAttributesStore((s) => s.attributes);
  const attributesLoaded = useAttributesStore((s) => !!s.syncState.lastSyncAt);

  useEffect(() => {
    if (given !== undefined || !workspaceId) return;
    let cancelled = false;
    loadFieldMappingConfig(workspaceId, connectorType)
      .then((config) => { if (!cancelled) setLoaded(resolveActiveMappingSet(config, flow)); })
      .catch(() => { /* no notice when the config cannot be read */ });
    return () => { cancelled = true; };
  }, [given, workspaceId, connectorType, flow]);

  const active = given !== undefined ? given : loaded;
  const set = active?.set ?? null;

  const attributeKeys = set && checkAttributes
    ? [...new Set(set.entries.filter((e) => ruleRunsOn(e, flow)).map((e) => attributeKeyOfTarget(e.internalFieldKey)).filter((k): k is string => !!k))]
    : [];

  useEffect(() => {
    if (attributeKeys.length > 0 && !attributesLoaded) void useAttributesStore.getState().fetchFromSupabase();
  }, [attributeKeys.length, attributesLoaded]);

  if (!set) {
    return emptyText ? <p className={`text-xs text-[var(--text-ter)] ${className}`}>{emptyText}</p> : null;
  }

  const scoring = attributes.filter((a) => !a.isCustomField);
  const labelOf = (key: string) => scoring.find((a) => a.key === key)?.label;
  const missing = attributesLoaded ? attributeKeys.filter((k) => !scoring.some((a) => a.key === k)) : [];
  const rules = describeMappingSetRules(set, connectorType, flow, labelOf);

  return (
    <div className={`space-y-1 text-xs text-[var(--text-ter)] ${className}`}>
      <p>
        Applying the “{set.name}” mapping set
        {active?.chosen ? '' : ` (none is picked for ${flow}, so the first set with ${flow} rules is used)`}.
        {rules ? <> {rules}.</> : ' It has no rules turned on yet.'}{' '}
        Change it under{' '}
        <Link to={FIELD_MAPPINGS_PATH} className="font-medium underline hover:no-underline">
          Settings → Field Mappings
        </Link>
        .
      </p>
      {missing.length > 0 && (
        <p className="text-[var(--coral-text)]">
          This workspace has no attribute called {missing.map((k) => `“${k}”`).join(' or ')}, so those values will be
          skipped. Pick another attribute in the set, or{' '}
          <Link to={ATTRIBUTES_PATH} className="font-medium underline hover:no-underline">
            add it in Settings → Attributes
          </Link>
          .
        </p>
      )}
    </div>
  );
}
